import { createClient } from '@supabase/supabase-js';
import { LinkedInAd, GoogleAd } from '../types/dataTypes';

// Get environment variables
const supabaseUrl = 'https://sdiuoxjuwoxrfqdietwz.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNkaXVveGp1d294cmZxZGlldHd6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc4MDA3NTUsImV4cCI6MjA2MzM3Njc1NX0.3jWN_vo1Zc6AhTbOIKdlhSNoddeypFjiAFEBLZpaEnI';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Helper to check connection and initialize data if needed
export const checkConnection = async () => {
  try {
    // Test connection by fetching a single row
    const { data, error } = await supabase
      .from('linkedin_ads')
      .select('*')
      .limit(1);

    if (error) {
      console.error('Supabase connection error:', error);
      return {
        connected: false,
        schemaInitialized: false,
        error: error.message
      };
    }

    // If we got data, we can infer the columns from the first row
    const columns = data && data[0] ? Object.keys(data[0]) : [];
    console.log('LinkedIn Ads Table Columns:', columns);

    return {
      connected: true,
      schemaInitialized: true,
      columns
    };
  } catch (error) {
    console.error('Error connecting to Supabase:', error);
    return {
      connected: false,
      schemaInitialized: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

export const checkCompanyExists = async (companyId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('linkedin_ads')
      .select('id')
      .eq('original_company_id', companyId)
      .limit(1);

    if (error) {
      console.error('Error checking company existence:', error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('Failed to check company existence:', error);
    return false;
  }
};

export const getCompanyName = async (companyId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('linkedin_ads')
      .select('og_company_name')
      .eq('original_company_id', companyId)
      .limit(1)
      .single();

    if (error) {
      console.error('Error fetching company name:', error);
      return null;
    }

    return data?.og_company_name || null;
  } catch (error) {
    console.error('Failed to fetch company name:', error);
    return null;
  }
};

export const fetchLinkedInAds = async (companyId: string): Promise<LinkedInAd[]> => {
  console.log('Fetching LinkedIn ads for company:', companyId);

  const { data, error } = await supabase
    .from('linkedin_ads')
    .select('*')
    .eq('original_company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching LinkedIn ads:', error);
    throw error;
  }

  // Map the database columns to our frontend interface
  const mappedData: LinkedInAd[] = data.map(row => ({
    id: row.id,
    original_company_id: row.original_company_id,
    imageURL: row.preview || '',
    headline: row.headline || '',
    body: row.body || '',
    ctas: row.ctas || '',
    format: row.format || '',
    advertiserName: row.advertiserName || '',
    targeting_location: row.geos || '',
    landing_page: row.landing_page || '',
    created_at: row.created_at
  }));

  console.log('LinkedIn ads data:', mappedData);
  return mappedData;
};

export const fetchGoogleAds = async (companyId: string): Promise<GoogleAd[]> => {
  console.log('Fetching Google ads for company:', companyId);
  
  try {
    const { data, error } = await supabase
      .from('google_ads')
      .select('*')
      .eq('original_company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching Google ads:', error);
      throw error;
    }

    console.log('Google ads data from database:', data);
    return data || [];
  } catch (error) {
    console.error('Failed to fetch Google ads:', error);
    throw error;
  }
};